import * as React from 'react';
import { Group, createStyles, NumberInput as NumberInputBase, NumberInputProps } from '@mantine/core';

// Components
import { FieldLabelTooltip } from './FieldLabelTooltip';

// Styles
import { getCommonFieldStyles } from '../helpers/fieldStyles';

// Hooks
import { useFieldTranslation } from '../hooks/useFieldTranslation';

// Types
import { GenericFieldProps } from '../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        inputHighlight: commonFieldStyles.inputHighlight,
        labelHighlight: commonFieldStyles.labelHighlight,
        readonlyInput: commonFieldStyles.readonlyInput,
        required: commonFieldStyles.required,
        hiddenDisplay: commonFieldStyles.hiddenDisplay,
        label: commonFieldStyles.label,
        icon: commonFieldStyles.icon,
        error: commonFieldStyles.error,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description,
        smallerInput: {
            width: 'auto'
        }
    };
});

export interface CustomNumberInputProps extends NumberInputProps, GenericFieldProps {
    key?: string;
    smallerInput?: boolean;
}

export const NumberInput: React.FC<CustomNumberInputProps> = ({
    readOnly,
    labelKey,
    label,
    descriptionKey,
    placeholderkey,
    tooltipKey,
    smallerInput,
    parentform,
    targetperiod,
    highlight,
    ...props
}) => {
    const { classes, cx } = useStyles();
    const inputLabel = useFieldTranslation({
        key: labelKey,
        label,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const descriptionLabel = useFieldTranslation({
        key: descriptionKey,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const placeholderLabel = useFieldTranslation({
        key: placeholderkey,
        formData: parentform?.values,
        targetPeriod: targetperiod,
        noInterpolation: true
    }) as string;

    return (
        <NumberInputBase
            classNames={{
                root: classes.root,
                input: cx(classes.input, {
                    [classes.readonlyInput]: readOnly,
                    [classes.smallerInput]: smallerInput,
                    [classes.inputHighlight]: typeof highlight === 'string' && highlight === 'true'
                }),
                required: cx(classes.required, {
                    [classes.hiddenDisplay]: readOnly
                }),
                label: cx(classes.label, {
                    [classes.labelHighlight]: typeof highlight === 'string' && highlight === 'true'
                }),
                icon: cx(classes.icon, {
                    [classes.hiddenDisplay]: readOnly
                }),
                wrapper: cx(classes.wrapper, {
                    [classes.readonlyWrapper]: readOnly
                }),
                error: classes.error,
                description: cx(classes.description, {
                    [classes.hiddenDisplay]: readOnly
                })
            }}
            type={'number'}
            label={
                <>
                    <span>{inputLabel}</span>
                    {tooltipKey ? <FieldLabelTooltip translationKey={tooltipKey} /> : null}
                </>
            }
            description={descriptionLabel}
            placeholder={placeholderLabel}
            {...props}
        />
    );
};
